import type { Mode, Plan, Point } from '@trippath/shared';
import {
  getMoonIllumination,
  getMoonPosition,
  getMoonTimes,
  getPosition,
  getTimes,
} from 'suncalc';

import { wall } from './clock';
import { arrivesAt, leavesAt } from './gaps';
import { bowAt, mercator } from './map';

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;
const DAY = 86_400_000;

/** Mercator's edge: as near the poles as the map draws. */
const EDGE = 85;

/** Where the sun stands overhead at an instant: its latitude the declination, its longitude
 * where it is noon (NOAA's approximation, within a tenth of a degree). */
export function subsolar(at: number): Point {
  const days = (at - Date.UTC(2000, 0, 1, 12)) / DAY;
  const mean = (280.46 + 0.985_647_4 * days) % 360;
  const anomaly = (357.528 + 0.985_600_3 * days) * RAD;
  const ecliptic = (mean + 1.915 * Math.sin(anomaly) + 0.02 * Math.sin(2 * anomaly)) * RAD;
  const tilt = (23.439 - 0.000_000_4 * days) * RAD;
  const lat = Math.asin(Math.sin(tilt) * Math.sin(ecliptic)) * DEG;
  const ascension = Math.atan2(Math.cos(tilt) * Math.sin(ecliptic), Math.cos(ecliptic)) * DEG;
  // The equation of time: how far the sun runs ahead of the clock, in degrees.
  const ahead = ((((mean - ascension) % 360) + 540) % 360) - 180;
  const minutes = (((at % DAY) + DAY) % DAY) / 60_000;
  const lon = ((((180 - minutes / 4 - ahead) % 360) + 540) % 360) - 180;
  return { lat, lon };
}

/** Whether the sun is down at a point, the sun overhead at `sun`: its height there under the
 * horizon, where the night starts to darken the map. */
export function sunDown(point: Point, sun: Point): boolean {
  const [here, over] = [point.lat * RAD, sun.lat * RAD];
  const height =
    Math.sin(here) * Math.sin(over) +
    Math.cos(here) * Math.cos(over) * Math.cos((point.lon - sun.lon) * RAD);
  return height < 0;
}

/** Near where the moon stands overhead at an instant, by its orbit roughly: its declination, and
 * the longitude its right ascension is overhead at. */
function nearMoon(at: number): Point {
  const days = (at - Date.UTC(2000, 0, 1, 12)) / DAY;
  const mean = (218.316 + 13.176_396 * days) * RAD;
  const anomaly = (134.963 + 13.064_993 * days) * RAD;
  const node = (93.272 + 13.22935 * days) * RAD;
  const lon = mean + 6.289 * RAD * Math.sin(anomaly);
  const lat = 5.128 * RAD * Math.sin(node);
  const tilt = 23.4397 * RAD;
  const declination = Math.asin(
    Math.sin(lat) * Math.cos(tilt) + Math.cos(lat) * Math.sin(tilt) * Math.sin(lon),
  );
  const ascension = Math.atan2(
    Math.sin(lon) * Math.cos(tilt) - Math.tan(lat) * Math.sin(tilt),
    Math.cos(lon),
  );
  // Greenwich's sidereal time: the right ascension overhead there.
  const sidereal = (280.16 + 360.985_623_5 * days) * RAD;
  const over = (((((ascension - sidereal) * DEG) % 360) + 540) % 360) - 180;
  return { lat: declination * DEG, lon: over };
}

/**
 * Where the moon stands overhead at an instant, as SunCalc sees it — the same moon its rises and
 * sets are told by: from where it is roughly, its height and bearing there turned back into the
 * place it stands straight above.
 */
export function sublunar(at: number): Point {
  // Twice: the second time from straight under it, where its parallax is none.
  const step = (near: Point): Point => {
    const seen = getMoonPosition(new Date(at), near.lat, near.lon);
    const [high, bearing] = [seen.altitude * RAD, seen.azimuth * RAD];
    const [sinLat, cosLat] = [Math.sin(near.lat * RAD), Math.cos(near.lat * RAD)];
    // In degrees, its bearing from the north clockwise, as SunCalc gives them.
    const declination = Math.asin(
      sinLat * Math.sin(high) + cosLat * Math.cos(high) * Math.cos(bearing),
    );
    const hour = Math.atan2(
      -Math.sin(bearing) * Math.cos(high),
      Math.sin(high) * cosLat - Math.cos(high) * Math.cos(bearing) * sinLat,
    );
    return {
      lat: declination * DEG,
      lon: ((((near.lon - hour * DEG) % 360) + 540) % 360) - 180,
    };
  };
  return step(step(nearMoon(at)));
}

/** The night's image: its width and height — Mercator's whole world, its rows as far apart as
 * the map draws them. */
export const NIGHT_SIZE = [1024, 1024] as const;

/** How the night darkens as the sun goes down: from each height, in degrees, so much darker —
 * the sun set, the civil, nautical and astronomical twilights. */
const STEPS: readonly (readonly [number, number])[] = [
  [0, 0.3],
  [-6, 0.25],
  [-12, 0.23],
  [-18, 0.22],
];
/** Over how many degrees of the sun's height each step is eased in. */
const EASE = 1;
/** How finely the sun's height is told apart, by its sine. */
const SHADES = 8192;

/** The corners the night's image is laid between, the sun overhead at `lon`: the image drawn
 * for the sun over Greenwich, moved along with it as the moment moves — one world wide, the
 * map's copies of the world beside it meeting it edge to edge. */
export function nightCorners(
  lon: number,
): [[number, number], [number, number], [number, number], [number, number]] {
  return [
    [lon - 180, EDGE],
    [lon + 180, EDGE],
    [lon + 180, -EDGE],
    [lon - 180, -EDGE],
  ];
}

/** How day and night are drawn over a map: in what colour, how strongly, and over which side —
 * the night darkened on a light map; on a dark map, already as dark as night, the day lit. */
export interface Shade {
  rgb: readonly [number, number, number];
  strength: number;
  lit: boolean;
}

/**
 * Day and night as an image's pixels, the sun overhead over Greenwich at latitude `tilt`: red,
 * green, blue and how strong — the night a step darker with each twilight, at its strongest once
 * the sun is 18° under the horizon; or, `lit`, the day so much brighter, its twilights a step
 * less each. Its image is laid where the sun is by `nightCorners`.
 */
export function nightShade(tilt: number, shade: Shade): Uint8ClampedArray {
  const [width, height] = NIGHT_SIZE;
  const [sinSun, cosSun] = [Math.sin(tilt * RAD), Math.cos(tilt * RAD)];
  const top = Math.log(Math.tan(Math.PI / 4 + (EDGE * RAD) / 2));
  const turns = Array.from({ length: width }, (_, x) =>
    Math.cos((-180 + ((x + 0.5) / width) * 360) * RAD),
  );
  // Each height of the sun a pixel's colour, looked up by its sine, no angle worked out for each
  // pixel: the night's blue a step darker at each of the civil, nautical and astronomical
  // twilights and at night, each step eased in over a degree so its edge is drawn smoothly.
  const shades = Uint32Array.from({ length: SHADES }, (_, at) => {
    const high = Math.asin((at / (SHADES - 1)) * 2 - 1) * DEG;
    const part = STEPS.reduce(
      (dark, [from, step]) =>
        dark + step * Math.min(1, Math.max(0, (from - high) / EASE + 0.5)),
      0,
    );
    const alpha = Math.round((shade.lit ? 1 - part : part) * shade.strength * 255);
    const [red, green, blue] = shade.rgb;
    // Little-endian, as every browser lays pixels out: red first.
    return ((alpha << 24) | (blue << 16) | (green << 8) | red) >>> 0;
  });
  const colours = new Uint32Array(width * height);
  for (let y = 0; y < height; y += 1) {
    // The row's latitude, Mercator's rows evenly apart.
    const lat = 2 * Math.atan(Math.exp(top - ((y + 0.5) / height) * 2 * top)) - Math.PI / 2;
    const [a, b] = [Math.sin(lat) * sinSun, Math.cos(lat) * cosSun];
    turns.forEach((turn, x) => {
      colours[y * width + x] = shades[
        Math.round((a + b * turn + 1) * ((SHADES - 1) / 2))
      ] as number;
    });
  }
  return new Uint8ClampedArray(colours.buffer);
}

/** A way of the plan said with its times and both ends on the map: how it goes, where it leaves
 * and arrives, and when. */
export interface Journey {
  stop: string;
  mode: Mode;
  from: Point;
  to: Point;
  leaves: number;
  lands: number;
  /** Its own track with each point's time (ms), from a file: where it was when — its times
   * then its leaving and arriving. */
  track?: { path: readonly [number, number][]; times: readonly number[] };
}

/** The plan's ways said with their times and both ends on the map, by whatever means; one with
 * a track timed point by point, by its track's times. */
export function journeysOf(plan: Plan): Journey[] {
  return plan.stops.flatMap((stop, at) => {
    const out = stop.out;
    const from = out?.from ?? stop.place;
    const to = out?.to ?? plan.stops[at + 1]?.place ?? null;
    // Its times as said for it, chosen or not yet.
    const [leaves, lands] = [leavesAt(stop), arrivesAt(stop)];
    if (!out || leaves === null || lands === null || !from || !to) return [];
    // Its own track's times, when its file said one for each point.
    const path = stop.track?.path ?? [];
    const times = (stop.track?.times ?? []).flatMap((time) =>
      time === null ? [] : [time * 1000],
    );
    const timed = times.length >= 2 && times.length === path.length;
    return [
      {
        stop: stop.id,
        mode: out.mode,
        from: { lat: from.lat, lon: from.lon },
        to: { lat: to.lat, lon: to.lon },
        leaves: timed ? (times[0] as number) : Date.parse(leaves),
        lands: timed ? (times[times.length - 1] as number) : Date.parse(lands),
        ...(timed ? { track: { path, times } } : {}),
      },
    ];
  });
}

/** Whoever is on their way at an instant — a plane, a train, a bus, a ship, a car, on foot:
 * where, which way, how far, whether the sun is up there, and the way come so far. */
export interface OnTheWay extends Point {
  heading: number;
  part: number;
  day: boolean;
  trail: [number, number][];
  journey: Journey;
}

/** How far along a line each of its points is, its degrees east shortened as the map's are
 * not, by the cosine of its latitude. */
function lengthsOf(path: readonly [number, number][]): number[] {
  const lengths = [0];
  for (let at = 1; at < path.length; at += 1) {
    const [[lon1, lat1], [lon2, lat2]] = [path[at - 1], path[at]] as [
      [number, number],
      [number, number],
    ];
    const east = (lon2 - lon1) * Math.cos(((lat1 + lat2) / 2) * RAD);
    lengths.push((lengths[at - 1] as number) + Math.hypot(east, lat2 - lat1));
  }
  return lengths;
}

/** How far along its track one was at an instant, by its points' times. */
function farAt(times: readonly number[], lengths: readonly number[], at: number): number {
  const next = Math.max(
    1,
    times.findIndex((time) => time > at),
  );
  const [from, to] = [times[next - 1], times[next]] as [number, number];
  const [near, far] = [lengths[next - 1], lengths[next]] as [number, number];
  return near + ((far - near) * (at - from)) / (to - from);
}

/** The point so far along a line, and how many of its points are passed. */
function pointAlong(
  path: readonly [number, number][],
  lengths: readonly number[],
  far: number,
): { point: [number, number]; passed: number } {
  const next = Math.max(
    1,
    lengths.findIndex((length) => length >= far),
  );
  const [[lon1, lat1], [lon2, lat2]] = [path[next - 1], path[next]] as [
    [number, number],
    [number, number],
  ];
  const [from, to] = [lengths[next - 1], lengths[next]] as [number, number];
  const part = to > from ? (far - from) / (to - from) : 0;
  return { point: [lon1 + (lon2 - lon1) * part, lat1 + (lat2 - lat1) * part], passed: next };
}

/**
 * Whoever is on their way at an instant, along the line the map draws for their way as `pathOf`
 * gives it — a track of its own, a road, rails — else a flight along its bow, else straight from
 * end to end; none when no one is on their way.
 */
export function onTheWayAt(
  journeys: readonly Journey[],
  at: number,
  pathOf: (journey: Journey) => readonly [number, number][] | null = () => null,
): OnTheWay | null {
  const journey = journeys.find((one) => one.leaves <= at && at < one.lands);
  if (!journey) return null;
  const part = (at - journey.leaves) / (journey.lands - journey.leaves);
  // The line drawn for its way — its own track from a file, its road or rails — else a flight's
  // bow, else straight.
  const path =
    journey.track?.path ??
    pathOf(journey) ??
    (journey.mode === 'flight'
      ? Array.from({ length: 65 }, (_, n) => bowAt(journey.from, journey.to, n / 64))
      : [
          [journey.from.lon, journey.from.lat],
          [journey.to.lon, journey.to.lat],
        ]);
  const lengths = lengthsOf(path);
  const whole = lengths[lengths.length - 1] as number;
  // Timed point by point: as far along as its track says it was then; else as far as the time.
  const far = journey.track ? farAt(journey.track.times, lengths, at) : part * whole;
  const {
    point: [lon, lat],
    passed,
  } = pointAlong(path, lengths, far);
  // Its heading as the map shows it, north up: from a little behind it to a little ahead.
  const near = whole / 100;
  const [behindLon, behindLat] = pointAlong(path, lengths, Math.max(0, far - near)).point;
  const [aheadLon, aheadLat] = pointAlong(path, lengths, Math.min(whole, far + near)).point;
  const heading =
    (Math.atan2(aheadLon - behindLon, mercator(aheadLat) - mercator(behindLat)) * DEG + 360) %
    360;
  return {
    lat,
    lon,
    heading,
    part,
    day: getPosition(new Date(at), lat, lon).altitude > 0,
    trail: [...path.slice(0, passed), [lon, lat]],
    journey,
  };
}

/** The sun rising or setting as seen from a plane on its way: when, which, and where it is. */
export interface SunTurn extends Point {
  at: number;
  rise: boolean;
}

/** How far under the horizon the sun sets as seen from a plane at its cruise, some ten
 * kilometres up: the horizon dips some three degrees there, refraction half a degree more. */
const CRUISE_SUNSET = -3.5;

/**
 * Where a plane on its way sees the sun rise or set: along its way each two minutes, where the
 * sun goes under or comes over the horizon as seen from its cruise.
 */
export function sunTurns(journey: Journey): SunTurn[] {
  const turns: SunTurn[] = [];
  let before: boolean | null = null;
  for (let at = journey.leaves; at < journey.lands; at += 120_000) {
    const { lat, lon } = onTheWayAt([journey], at) as OnTheWay;
    const day = getPosition(new Date(at), lat, lon).altitude > CRUISE_SUNSET;
    if (before !== null && day !== before) turns.push({ at, rise: day, lat, lon });
    before = day;
  }
  return turns;
}

/** The moon's shape as it is called, by its phase from new (0) through full (0.5). */
export type MoonShape =
  | 'new'
  | 'waxingCrescent'
  | 'firstQuarter'
  | 'waxingGibbous'
  | 'full'
  | 'waningGibbous'
  | 'lastQuarter'
  | 'waningCrescent';

export function shapeOf(phase: number): MoonShape {
  const named: [number, MoonShape][] = [
    [0.0339, 'new'],
    [0.216, 'waxingCrescent'],
    [0.284, 'firstQuarter'],
    [0.466, 'waxingGibbous'],
    [0.534, 'full'],
    [0.716, 'waningGibbous'],
    [0.784, 'lastQuarter'],
    [0.966, 'waningCrescent'],
  ];
  return named.find(([until]) => phase < until)?.[1] ?? 'new';
}

/** A day's sky at a place, by its own clock: the moon in its phase, how it is turned there and
 * when it is up. */
export interface SkyDay {
  shape: MoonShape;
  phase: number;
  /** How the moon is turned as it is seen there, degrees clockwise from its drawing — its lit
   * side to the right while it waxes: a crescent upright in Tallinn, lying like a boat in
   * Bangkok, upside down south of the equator. */
  turn: number;
  /** Whether it can be seen there that night: up while the sky is dark, more than a sliver lit. */
  visible: boolean;
  /** When it rises before it is seen that night and sets after, by the stop's clock; a dash
   * when it is not seen. */
  moonrise: string;
  moonset: string;
  /** The day it rises, counted from that day — the next after midnight — and the days it sets
   * after it rises. */
  riseDays: number;
  setDays: number;
  /** Up all day and night, as far north as it may be: neither rising nor setting that day. */
  moonAllDay: boolean;
  /** The sun by the stop's clock: when it rises and sets — none when it does not, all day up or
   * all day down — when it stands highest, and how many minutes of the day it is up. */
  sunrise: string | null;
  sunset: string | null;
  solarNoon: string;
  daylight: number;
}

/** Each day's sky at each place, worked out once: a calendar asks again each time it is drawn. */
const skies = new Map<string, SkyDay>();

export function skyDay(day: string, place: Point, zone: string): SkyDay {
  const key = `${day} ${String(place.lat)} ${String(place.lon)} ${zone}`;
  const known = skies.get(key);
  if (known) return known;
  const worked = workSky(day, place, zone);
  skies.set(key, worked);
  return worked;
}

function workSky(day: string, place: Point, zone: string): SkyDay {
  const midnight = Date.parse(`${day}T00:00:00Z`);
  // Its clock's offset that day, in minutes east of UTC.
  const offset = Math.round((wall(zone, midnight + DAY / 2) - (midnight + DAY / 2)) / 60_000);
  const noon = new Date(midnight + DAY / 2 - offset * 60_000);
  const moon = getMoonIllumination(noon);
  const sun = getTimes(noon, place.lat, place.lon, 0, offset);
  // The moon of the day, as a calendar of the sky gives it: the one rising that day by the
  // stop's clock — else, risen the day before, the one up as the day begins — and its first set
  // after, often the next day.
  const near = (days: number) =>
    getMoonTimes(new Date(noon.getTime() + days * DAY), place.lat, place.lon, offset);
  const today = near(0);
  const times = [near(-1), today, near(1)];
  const rises = times.flatMap((one) => (one.rise ? [one.rise] : []));
  const sets = times.flatMap((one) => (one.set ? [one.set] : []));
  const start = noon.getTime() - DAY / 2;
  const rise =
    rises.find((at) => at.getTime() >= start && at.getTime() < start + DAY) ??
    rises.findLast((at) => at.getTime() < start);
  const set = rise && sets.find((at) => at > rise);
  // While it is up, each half hour: the first time it stands in a dark sky. Never rising or
  // setting, as far north as it may not, through the day.
  const [from, until] = [rise?.getTime() ?? start, set?.getTime() ?? start + DAY];
  const seen = Array.from(
    { length: Math.floor((until - from) / 1_800_000) + 1 },
    (_, step) => new Date(from + step * 1_800_000),
  ).find(
    (at) =>
      getPosition(at, place.lat, place.lon).altitude < -6 &&
      getMoonPosition(at, place.lat, place.lon).altitude > 2,
  );
  // Its lit side's angle from the zenith there, anticlockwise, turned into how far the drawing
  // turns, clockwise.
  const looked = seen ?? noon;
  const zenith =
    getMoonIllumination(looked).angle -
    getMoonPosition(looked, place.lat, place.lon).parallacticAngle;
  const drawn = moon.phase < 0.5 ? -90 : 90;
  // Each by the stop's clock and the day it falls on, counted from that day.
  const placed = (at: Date | undefined) => {
    if (at === undefined) return { time: '—', days: 0 };
    const there = new Date(wall(zone, at.getTime())).toISOString();
    return {
      time: there.slice(11, 16),
      days: (Date.parse(there.slice(0, 10)) - midnight) / DAY,
    };
  };
  const [up, down] = [placed(rise), placed(set)];
  const clock = (at: Date) => new Date(wall(zone, at.getTime())).toISOString().slice(11, 16);
  const [sunrise, sunset] = [sun.sunrise, sun.sunset];
  return {
    shape: shapeOf(moon.phase),
    phase: moon.phase,
    turn: Math.round((((drawn - zenith) % 360) + 360) % 360),
    visible: seen !== undefined && moon.fraction >= 0.03,
    moonrise: up.time,
    moonset: down.time,
    riseDays: up.days,
    setDays: set ? down.days - up.days : 0,
    moonAllDay: today.alwaysUp === true,
    sunrise: sunrise ? clock(sunrise) : null,
    sunset: sunset ? clock(sunset) : null,
    solarNoon: clock(sun.solarNoon),
    // Up from its rise to its set; never set, all day; never risen, none.
    daylight:
      sunrise && sunset
        ? Math.round((sunset.getTime() - sunrise.getTime()) / 60_000)
        : sun.alwaysUp
          ? 1440
          : 0,
  };
}
